import { Box } from '@mui/material';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getActiveTicket, getIsTyping } from 'store/ticket-service/selector';

import { useEventBus } from 'utils/EventBus';

import { NamespacesList, SharePermissions } from 'core/enums/common';

import styles from './Chatbot.module.scss';
import { IMessage } from './Message/types';
import { MessageInput } from './MessageInput/MessageInput';
import { MessageList } from './MessageList/MessageList';

interface ChatbotProps {
	messages: Array<IMessage>;
	setMessageValue: (value: string) => void;
	messageValue: string;
	waitingProceeding: boolean;
	isChatLoading: boolean;
}

export const Chatbot: FC<ChatbotProps> = ({
	messages,
	setMessageValue,
	messageValue,
	waitingProceeding,
	isChatLoading,
}) => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const isTyping = useSelector(getIsTyping);
	const activeTicket = useSelector(getActiveTicket);

	const { dispatchEvent } = useEventBus();
	const { setValue } = useFormContext();

	const inputRef = useRef<HTMLInputElement | null>(null);
	const chatWindowRef = useRef<HTMLDivElement | null>(null);

	const isMobile = useMemo(() => window.innerWidth < 500, []);

	useEffect(() => {
		if (chatWindowRef.current) {
			chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
		}
		if (inputRef.current && !isTyping && !isMobile) {
			inputRef.current.focus();
		}
	}, [isTyping, isMobile]);

	useEffect(() => {
		chatWindowRef.current!.scrollTop = chatWindowRef.current!.scrollHeight;
		setValue('messages', messages);
	}, [messages, setValue]);

	const handleMessageSend = (message: IMessage) => {
		dispatchEvent('messageSend', message);
	};

	const isShareReadOnlyPermission = useMemo(
		() =>
			activeTicket?.permissions
				?.map((permission) => permission.permission)
				.includes(SharePermissions.Read),
		[activeTicket?.id]
	);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				height: '100%',
				width: { xs: '100%', md: '99%' },
			}}
		>
			<Box
				className={styles.chat}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					flex: '0 1 100%',
					minHeight: { xs: '0', md: '300px' },
					backgroundColor: '#EFEFEF',
				}}
			>
				<MessageList isChatLoading={isChatLoading} list={messages} ref={chatWindowRef} />

				<Box
					sx={{
						margin: '16px 12px 12px 12px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						gap: '16px',
					}}
				>
					<MessageInput
						ref={inputRef}
						placeholder={commonTranslate('inputs.chatInputPlaceholder') as string}
						onSend={(value) => handleMessageSend(value)}
						setValue={setMessageValue}
						value={messageValue}
						disabled={isChatLoading || isTyping || waitingProceeding || isShareReadOnlyPermission}
					/>
				</Box>
			</Box>
		</Box>
	);
};
